<template>
  <header class="slim-header">
    <h1 class="page-title">{{ currentTitle }}</h1>
    <div class="header-right">
      <span v-html="envBadgeHTML"></span>
      <div class="header-avatar" :style="avatarStyle">
        <div v-if="!avatarUrl" class="avatar-initials">
          {{ userInitials }}
        </div>
      </div>
      <span class="header-username">{{ authStore.nombre }}</span>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from '../../stores/auth.js'
import { currentTitle } from '../../composables/usePageTitle.js'
import envBadge from '../../utils/environmentBadge.js'

const authStore = useAuthStore()

envBadge.init()
const envBadgeHTML = envBadge.getBadgeHTML()

// Avatar desde S3
const avatarUrl = ref(null)

const avatarStyle = computed(() => {
  if (avatarUrl.value) {
    return {
      backgroundImage: `url(${avatarUrl.value}?t=${Date.now()})`
    }
  }
  return {}
})

const userInitials = computed(() => {
  const email = authStore.userMail || ''
  const username = email.includes('@') ? email.split('@')[0] : email
  return username.substring(0, 2).toUpperCase()
})

function checkImageExists(url) {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
    img.src = url
  })
}

async function checkUserAvatar() {
  const userId = authStore.userId
  if (!userId) return false

  const s3BaseUrl = 'https://s3.sa-east-1.amazonaws.com/srk.varios/'
  const extensions = ['png', 'jpeg']

  for (const ext of extensions) {
    const imageUrl = `${s3BaseUrl}imgUser${userId}.${ext}`
    try {
      const imageExists = await checkImageExists(imageUrl)
      if (imageExists) {
        avatarUrl.value = imageUrl
        return true
      }
    } catch (error) {
      continue
    }
  }
  return false
}

function handleAvatarUpdate(event) {
  const url = event.detail.url.split('?')[0]
  avatarUrl.value = url
}

function handleAvatarRemoved() {
  avatarUrl.value = null
}

onMounted(async () => {
  await checkUserAvatar()
  window.addEventListener('avatar-updated', handleAvatarUpdate)
  window.addEventListener('avatar-removed', handleAvatarRemoved)
})

watch(() => authStore.userId, async (newUserId, oldUserId) => {
  if (newUserId && newUserId !== oldUserId) {
    await checkUserAvatar()
  }
})

onUnmounted(() => {
  window.removeEventListener('avatar-updated', handleAvatarUpdate)
  window.removeEventListener('avatar-removed', handleAvatarRemoved)
})
</script>

<style scoped>
.slim-header {
  height: var(--header-height);
  background: var(--header-bg);
  border-bottom: 1px solid var(--header-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--content-padding);
  flex-shrink: 0;
}

.page-title {
  font-size: var(--font-size-lg);
  font-weight: 500;
  color: var(--srk-text-primary);
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: var(--srk-primary-light);
  background-size: cover;
  background-position: center;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: var(--srk-primary);
}

.header-username {
  font-size: var(--font-size-sm);
  color: var(--srk-text-secondary);
  font-weight: 500;
}
</style>
